import { apiFetch, apiFetchBlob } from "@/lib/api";

/** Standard Django envelope; list endpoints add `meta`. */
interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

// --- Company / firm profile (GET/PATCH /firm/) ---------------------------

export interface FirmProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  billDiff: boolean;
  billAddressLine1: string;
  billAddressLine2: string;
  billCity: string;
  billState: string;
  billZipCode: string;
}

interface ApiFirm {
  id: string;
  name: string;
  email: string;
  phone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip_code: string;
  bill_diff: boolean;
  bill_address_line1: string;
  bill_address_line2: string;
  bill_city: string;
  bill_state: string;
  bill_zip_code: string;
}

function toFirmProfile(f: ApiFirm): FirmProfile {
  return {
    id: f.id,
    name: f.name,
    email: f.email,
    phone: f.phone,
    addressLine1: f.address_line1,
    addressLine2: f.address_line2,
    city: f.city,
    state: f.state,
    zipCode: f.zip_code,
    billDiff: f.bill_diff,
    billAddressLine1: f.bill_address_line1,
    billAddressLine2: f.bill_address_line2,
    billCity: f.bill_city,
    billState: f.bill_state,
    billZipCode: f.bill_zip_code,
  };
}

export async function getFirm(): Promise<FirmProfile> {
  const { data } = await apiFetch<Envelope<ApiFirm>>("/firm/");
  return toFirmProfile(data);
}

export async function updateFirm(
  payload: Omit<FirmProfile, "id">,
): Promise<FirmProfile> {
  const { data } = await apiFetch<Envelope<ApiFirm>>("/firm/", {
    method: "PATCH",
    json: {
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
      address_line1: payload.addressLine1,
      address_line2: payload.addressLine2,
      city: payload.city,
      state: payload.state,
      zip_code: payload.zipCode,
      bill_diff: payload.billDiff,
      bill_address_line1: payload.billAddressLine1,
      bill_address_line2: payload.billAddressLine2,
      bill_city: payload.billCity,
      bill_state: payload.billState,
      bill_zip_code: payload.billZipCode,
    },
  });
  return toFirmProfile(data);
}

// --- Firm settings (GET/PATCH /firm/settings) ----------------------------
// Backs the Invoices, Client Portal and Defaults tabs.

export type InvoiceTerms = "RECEIPT" | "NET7" | "NET21" | "NET30";

export const INVOICE_TERMS_OPTIONS: { value: InvoiceTerms; label: string }[] = [
  { value: "RECEIPT", label: "Due on receipt" },
  { value: "NET7", label: "Net 7 days" },
  { value: "NET21", label: "Net 21 days" },
  { value: "NET30", label: "Net 30 days" },
];

export interface PoaMatter {
  description: string;
  form: string;
  years: string;
}

/** One default document the firm requests from a new client of a given type. */
export interface RequiredDocDefault {
  name: string;
  notes: string;
}

export interface RequiredDocumentsDefaults {
  individual: RequiredDocDefault[];
  business: RequiredDocDefault[];
}

export type LogoAlign = "L" | "C" | "R";

export type LetterFont = "HELVETICA" | "TIMES";

export const LETTER_FONT_OPTIONS: { value: LetterFont; label: string }[] = [
  { value: "HELVETICA", label: "Arial / Helvetica" },
  { value: "TIMES", label: "Times New Roman" },
];

export const ALIGN_OPTIONS: { value: LogoAlign; label: string }[] = [
  { value: "L", label: "Left" },
  { value: "C", label: "Center" },
  { value: "R", label: "Right" },
];

/** A node in the Default Folder Structure tree (same shape on the wire). */
export interface FolderTemplateNode {
  name: string;
  children: FolderTemplateNode[];
}

export interface FolderTemplate {
  individual: FolderTemplateNode[];
  business: FolderTemplateNode[];
}

export interface FirmSettings {
  /** URL of the uploaded firm logo (null when none). Read-only. */
  logoUrl: string | null;
  logoScale: number;
  logoAlign: LogoAlign;
  invoiceDefaultTaxPercent: string;
  invoiceDefaultTerms: InvoiceTerms;
  invoiceFooter: string;
  // --- Invoices › PDF rendering ------------------------------------------
  invoiceUseTax: boolean;
  invoiceNextNumber: number;
  invoiceHeader: string;
  invoiceHeaderAlign: LogoAlign;
  invoiceTopMargin: number;
  invoiceUseLogo: boolean;
  /** URL of the uploaded invoice logo (null when none). Read-only. */
  invoiceLogoUrl: string | null;
  invoiceLogoAlign: LogoAlign;
  invoiceLogoScale: number;
  invoiceLogoMarginBottom: number;
  invoiceUseQr: boolean;
  invoiceQrPrompt: string;
  /** URL of the uploaded payment-QR image (null when none). Read-only. */
  invoiceQrImageUrl: string | null;
  portalEnabled: boolean;
  portalName: string;
  portalShowName: boolean;
  portalFooterLeft: string;
  portalFooterRight: string;
  poaRemoveRepName: boolean;
  poaUseRepPhone: boolean;
  poaCopyRep1: boolean;
  poaCopyRep2: boolean;
  poaAuthAccess: boolean;
  poaMattersIndividual: PoaMatter[];
  poaMattersBusiness: PoaMatter[];
  /** Default workflow steps a New Case pre-populates, keyed by resolution type. */
  defaultCaseSteps: Record<string, string[]>;
  /** Documents the onboarding "Request docs" step pre-fills, by client type. */
  defaultRequiredDocuments: RequiredDocumentsDefaults;
  /** The seeded preset (read-only) — backs "Restore defaults" for requested docs. */
  requiredDocumentsSeed: RequiredDocumentsDefaults;
  // --- E-Signature (04-4) ------------------------------------------------
  /** How many signature requests the firm may consume (packs = Tier 5). */
  esignatureQuota: number;
  /** Invitation email subject/body that pre-fills the Send modal ({link} merge). */
  esignEmailSubject: string;
  esignEmailBody: string;
  // --- e-Services / Integrations (05-2) ---------------------------------
  /** When on, the transcript-request queue + e-Services hub show the outage
   * banner and block queueing. */
  eservicesDown: boolean;
  eservicesMessage: string;
  /** IRS e-Filing (Submission Center) firm opt-in + outage gating. */
  efilingEnabled: boolean;
  efilingDown: boolean;
  efilingMessage: string;
  /** Per-client-type folder tree scaffolded onto each new client's archive. */
  folderTemplate: FolderTemplate;
  /** The seeded default (read-only) — backs "Restore to defaults". */
  defaultFolderTemplate: FolderTemplate;
  // --- Letters › PDF print layout / letterhead ---------------------------
  letterFont: LetterFont;
  letterHeader: string;
  letterHeaderAlign: LogoAlign;
  letterFooter: string;
  letterTopMargin: number;
  letterUseLogo: boolean;
  /** URL of the uploaded letterhead logo (null when none). Read-only. */
  letterLogoUrl: string | null;
  letterLogoAlign: LogoAlign;
  letterLogoScale: number;
  letterLogoSpaceBelow: number;
  logoDpi: number;
}

interface ApiFirmSettings {
  logo: string | null;
  logo_scale: number;
  logo_align: LogoAlign;
  invoice_default_tax_percent: string;
  invoice_default_terms: InvoiceTerms;
  invoice_footer: string;
  invoice_use_tax: boolean;
  invoice_next_number: number;
  invoice_header: string;
  invoice_header_align: LogoAlign;
  invoice_top_margin: number;
  invoice_use_logo: boolean;
  invoice_logo: string | null;
  invoice_logo_align: LogoAlign;
  invoice_logo_scale: number;
  invoice_logo_margin_bottom: number;
  invoice_use_qr: boolean;
  invoice_qr_prompt: string;
  invoice_qr_image: string | null;
  portal_enabled: boolean;
  portal_name: string;
  portal_show_name: boolean;
  portal_footer_left: string;
  portal_footer_right: string;
  poa_remove_rep_name: boolean;
  poa_use_rep_phone: boolean;
  poa_copy_rep1: boolean;
  poa_copy_rep2: boolean;
  poa_auth_access: boolean;
  poa_matters_individual: PoaMatter[];
  poa_matters_business: PoaMatter[];
  default_case_steps: Record<string, string[]>;
  default_required_documents: RequiredDocumentsDefaults;
  required_documents_seed: RequiredDocumentsDefaults;
  esignature_quota: number;
  esign_email_subject: string;
  esign_email_body: string;
  eservices_down: boolean;
  eservices_message: string;
  efiling_enabled: boolean;
  efiling_down: boolean;
  efiling_message: string;
  folder_template: FolderTemplate;
  default_folder_template: FolderTemplate;
  letter_font: LetterFont;
  letter_header: string;
  letter_header_align: LogoAlign;
  letter_footer: string;
  letter_top_margin: number;
  letter_use_logo: boolean;
  letter_logo: string | null;
  letter_logo_align: LogoAlign;
  letter_logo_scale: number;
  letter_logo_space_below: number;
  logo_dpi: number;
}

function toFirmSettings(s: ApiFirmSettings): FirmSettings {
  return {
    logoUrl: s.logo,
    logoScale: s.logo_scale,
    logoAlign: s.logo_align,
    invoiceDefaultTaxPercent: s.invoice_default_tax_percent,
    invoiceDefaultTerms: s.invoice_default_terms,
    invoiceFooter: s.invoice_footer,
    invoiceUseTax: s.invoice_use_tax,
    invoiceNextNumber: s.invoice_next_number,
    invoiceHeader: s.invoice_header,
    invoiceHeaderAlign: s.invoice_header_align,
    invoiceTopMargin: s.invoice_top_margin,
    invoiceUseLogo: s.invoice_use_logo,
    invoiceLogoUrl: s.invoice_logo,
    invoiceLogoAlign: s.invoice_logo_align,
    invoiceLogoScale: s.invoice_logo_scale,
    invoiceLogoMarginBottom: s.invoice_logo_margin_bottom,
    invoiceUseQr: s.invoice_use_qr,
    invoiceQrPrompt: s.invoice_qr_prompt,
    invoiceQrImageUrl: s.invoice_qr_image,
    portalEnabled: s.portal_enabled,
    portalName: s.portal_name,
    portalShowName: s.portal_show_name,
    portalFooterLeft: s.portal_footer_left,
    portalFooterRight: s.portal_footer_right,
    poaRemoveRepName: s.poa_remove_rep_name,
    poaUseRepPhone: s.poa_use_rep_phone,
    poaCopyRep1: s.poa_copy_rep1,
    poaCopyRep2: s.poa_copy_rep2,
    poaAuthAccess: s.poa_auth_access,
    poaMattersIndividual: s.poa_matters_individual,
    poaMattersBusiness: s.poa_matters_business,
    defaultCaseSteps: s.default_case_steps ?? {},
    defaultRequiredDocuments: s.default_required_documents ?? { individual: [], business: [] },
    requiredDocumentsSeed: s.required_documents_seed ?? { individual: [], business: [] },
    esignatureQuota: s.esignature_quota,
    esignEmailSubject: s.esign_email_subject,
    esignEmailBody: s.esign_email_body,
    eservicesDown: s.eservices_down,
    eservicesMessage: s.eservices_message,
    efilingEnabled: s.efiling_enabled,
    efilingDown: s.efiling_down,
    efilingMessage: s.efiling_message,
    folderTemplate: s.folder_template ?? { individual: [], business: [] },
    defaultFolderTemplate: s.default_folder_template ?? {
      individual: [],
      business: [],
    },
    letterFont: s.letter_font,
    letterHeader: s.letter_header,
    letterHeaderAlign: s.letter_header_align,
    letterFooter: s.letter_footer,
    letterTopMargin: s.letter_top_margin,
    letterUseLogo: s.letter_use_logo,
    letterLogoUrl: s.letter_logo,
    letterLogoAlign: s.letter_logo_align,
    letterLogoScale: s.letter_logo_scale,
    letterLogoSpaceBelow: s.letter_logo_space_below,
    logoDpi: s.logo_dpi,
  };
}

// camelCase → snake_case for the writable keys; only present keys are sent so
// each tab can PATCH just its own slice of the settings row.
const FIELD_MAP: Record<keyof FirmSettings, string> = {
  logoUrl: "logo",
  logoScale: "logo_scale",
  logoAlign: "logo_align",
  invoiceDefaultTaxPercent: "invoice_default_tax_percent",
  invoiceDefaultTerms: "invoice_default_terms",
  invoiceFooter: "invoice_footer",
  invoiceUseTax: "invoice_use_tax",
  invoiceNextNumber: "invoice_next_number",
  invoiceHeader: "invoice_header",
  invoiceHeaderAlign: "invoice_header_align",
  invoiceTopMargin: "invoice_top_margin",
  invoiceUseLogo: "invoice_use_logo",
  invoiceLogoUrl: "invoice_logo",
  invoiceLogoAlign: "invoice_logo_align",
  invoiceLogoScale: "invoice_logo_scale",
  invoiceLogoMarginBottom: "invoice_logo_margin_bottom",
  invoiceUseQr: "invoice_use_qr",
  invoiceQrPrompt: "invoice_qr_prompt",
  invoiceQrImageUrl: "invoice_qr_image",
  portalEnabled: "portal_enabled",
  portalName: "portal_name",
  portalShowName: "portal_show_name",
  portalFooterLeft: "portal_footer_left",
  portalFooterRight: "portal_footer_right",
  poaRemoveRepName: "poa_remove_rep_name",
  poaUseRepPhone: "poa_use_rep_phone",
  poaCopyRep1: "poa_copy_rep1",
  poaCopyRep2: "poa_copy_rep2",
  poaAuthAccess: "poa_auth_access",
  poaMattersIndividual: "poa_matters_individual",
  poaMattersBusiness: "poa_matters_business",
  defaultCaseSteps: "default_case_steps",
  defaultRequiredDocuments: "default_required_documents",
  requiredDocumentsSeed: "required_documents_seed",
  esignatureQuota: "esignature_quota",
  esignEmailSubject: "esign_email_subject",
  esignEmailBody: "esign_email_body",
  eservicesDown: "eservices_down",
  eservicesMessage: "eservices_message",
  efilingEnabled: "efiling_enabled",
  efilingDown: "efiling_down",
  efilingMessage: "efiling_message",
  folderTemplate: "folder_template",
  defaultFolderTemplate: "default_folder_template",
  letterFont: "letter_font",
  letterHeader: "letter_header",
  letterHeaderAlign: "letter_header_align",
  letterFooter: "letter_footer",
  letterTopMargin: "letter_top_margin",
  letterUseLogo: "letter_use_logo",
  letterLogoUrl: "letter_logo",
  letterLogoAlign: "letter_logo_align",
  letterLogoScale: "letter_logo_scale",
  letterLogoSpaceBelow: "letter_logo_space_below",
  logoDpi: "logo_dpi",
};

export async function getFirmSettings(): Promise<FirmSettings> {
  const { data } = await apiFetch<Envelope<ApiFirmSettings>>("/firm/settings");
  return toFirmSettings(data);
}

export async function updateFirmSettings(
  patch: Partial<FirmSettings>,
): Promise<FirmSettings> {
  const body: Record<string, unknown> = {};
  for (const key of Object.keys(patch) as (keyof FirmSettings)[]) {
    body[FIELD_MAP[key]] = patch[key];
  }
  const { data } = await apiFetch<Envelope<ApiFirmSettings>>("/firm/settings", {
    method: "PATCH",
    json: body,
  });
  return toFirmSettings(data);
}

/**
 * POST /firm/settings/folder-template/apply — apply the saved folder template to
 * existing clients. With `clientId` it runs inline (200); without it, all clients
 * are rebuilt in a background job (202 "queued").
 */
export async function applyFolderTemplate(
  clientId?: string,
): Promise<{ queued: boolean; message?: string }> {
  const { message, data } = await apiFetch<
    Envelope<{ applied?: number; queued?: boolean; message?: string }>
  >("/firm/settings/folder-template/apply", {
    method: "POST",
    json: clientId ? { client_id: clientId } : {},
  });
  return { queued: Boolean(data.queued), message: data.message ?? message };
}

/**
 * POST /firm/settings/restore-poa-defaults/{ind|bus} — reset one section of the
 * POA/TIA tax-matter defaults to the system preset. Returns the updated settings.
 */
export async function restorePoaDefaults(
  section: "ind" | "bus",
): Promise<FirmSettings> {
  const { data } = await apiFetch<Envelope<ApiFirmSettings>>(
    `/firm/settings/restore-poa-defaults/${section}`,
    { method: "POST" },
  );
  return toFirmSettings(data);
}

/** PATCH /firm/settings (multipart) — upload/replace the firm logo image. */
export async function uploadFirmLogo(file: File): Promise<FirmSettings> {
  const form = new FormData();
  form.append("logo", file);
  const { data } = await apiFetch<Envelope<ApiFirmSettings>>("/firm/settings", {
    method: "PATCH",
    form,
  });
  return toFirmSettings(data);
}

/** PATCH /firm/settings (multipart) — upload/replace the letterhead logo image. */
export async function uploadLetterLogo(file: File): Promise<FirmSettings> {
  const form = new FormData();
  form.append("letter_logo", file);
  const { data } = await apiFetch<Envelope<ApiFirmSettings>>("/firm/settings", {
    method: "PATCH",
    form,
  });
  return toFirmSettings(data);
}

/** PATCH /firm/settings (multipart) — upload/replace the invoice logo image. */
export async function uploadInvoiceLogo(file: File): Promise<FirmSettings> {
  const form = new FormData();
  form.append("invoice_logo", file);
  const { data } = await apiFetch<Envelope<ApiFirmSettings>>("/firm/settings", {
    method: "PATCH",
    form,
  });
  return toFirmSettings(data);
}

/** PATCH /firm/settings (multipart) — upload/replace the payment-QR image. */
export async function uploadInvoiceQr(file: File): Promise<FirmSettings> {
  const form = new FormData();
  form.append("invoice_qr_image", file);
  const { data } = await apiFetch<Envelope<ApiFirmSettings>>("/firm/settings", {
    method: "PATCH",
    form,
  });
  return toFirmSettings(data);
}

/** POST /firm/settings/letterhead-preview — sample letter PDF (current settings). */
export function letterheadPreview(): Promise<Blob> {
  return apiFetchBlob("/firm/settings/letterhead-preview", { method: "POST" });
}

// --- Firm signatures (GET/POST/PATCH/DELETE /firm/signatures/) ------------

export interface FirmSignature {
  id: string;
  name: string;
  imageUrl: string | null;
  scale: number;
  isDefault: boolean;
}

interface ApiFirmSignature {
  id: string;
  name: string;
  image: string | null;
  scale: number;
  is_default: boolean;
}

function toSignature(s: ApiFirmSignature): FirmSignature {
  return {
    id: s.id,
    name: s.name,
    imageUrl: s.image,
    scale: s.scale,
    isDefault: s.is_default,
  };
}

export async function listFirmSignatures(): Promise<FirmSignature[]> {
  const { data } = await apiFetch<Envelope<ApiFirmSignature[]>>(
    "/firm/signatures/?page_size=100",
  );
  return data.map(toSignature);
}

export async function createFirmSignature(input: {
  name: string;
  file: File;
  scale: number;
  isDefault: boolean;
}): Promise<FirmSignature> {
  const form = new FormData();
  form.append("name", input.name);
  form.append("image", input.file);
  form.append("scale", String(input.scale));
  form.append("is_default", input.isDefault ? "true" : "false");
  const { data } = await apiFetch<Envelope<ApiFirmSignature>>("/firm/signatures/", {
    method: "POST",
    form,
  });
  return toSignature(data);
}

export async function updateFirmSignature(
  id: string,
  patch: { name?: string; scale?: number; isDefault?: boolean },
): Promise<FirmSignature> {
  const json: Record<string, unknown> = {};
  if (patch.name !== undefined) json.name = patch.name;
  if (patch.scale !== undefined) json.scale = patch.scale;
  if (patch.isDefault !== undefined) json.is_default = patch.isDefault;
  const { data } = await apiFetch<Envelope<ApiFirmSignature>>(
    `/firm/signatures/${id}/`,
    { method: "PATCH", json },
  );
  return toSignature(data);
}

export function deleteFirmSignature(id: string): Promise<void> {
  return apiFetch<void>(`/firm/signatures/${id}/`, { method: "DELETE" });
}

// --- API keys (GET/POST/DELETE /api-keys/) -------------------------------

export interface ApiKey {
  id: number;
  name: string;
  maskedKey: string;
  scopes: string[];
  isActive: boolean;
  expiresAt: string | null;
  rateLimit: string;
  createdAt: string;
  lastUsedAt: string | null;
}

interface ApiApiKey {
  id: number;
  name: string;
  masked_key: string;
  scopes: string[];
  is_active: boolean;
  expires_at: string | null;
  rate_limit: string;
  created_at: string;
  last_used_at: string | null;
}

function toApiKey(k: ApiApiKey): ApiKey {
  return {
    id: k.id,
    name: k.name,
    maskedKey: k.masked_key,
    scopes: k.scopes,
    isActive: k.is_active,
    expiresAt: k.expires_at,
    rateLimit: k.rate_limit,
    createdAt: k.created_at,
    lastUsedAt: k.last_used_at,
  };
}

export async function listApiKeys(): Promise<ApiKey[]> {
  const { data } = await apiFetch<Envelope<ApiApiKey[]>>("/api-keys/?page_size=100");
  return data.map(toApiKey);
}

/** Returns the raw secret (shown once) plus the stored key metadata. */
export async function createApiKey(
  name: string,
  scopes: string[],
): Promise<{ rawKey: string; apiKey: ApiKey }> {
  const { data } = await apiFetch<Envelope<{ key: string; api_key: ApiApiKey }>>(
    "/api-keys/",
    { method: "POST", json: { name, scopes } },
  );
  return { rawKey: data.key, apiKey: toApiKey(data.api_key) };
}

export function revokeApiKey(id: number): Promise<void> {
  return apiFetch<void>(`/api-keys/${id}/`, { method: "DELETE" });
}
